import {
  ReactElement,
  useCallback,
  useMemo,
} from "react";
import {
  DataGridPro,
  GridActionsCellItem,
  GridActionsCellItemProps,
  GridColDef,
  GridToolbarColumnsButton,
  GridToolbarContainer,
  GridToolbarFilterButton,
  GridToolbarQuickFilter,
  getGridSingleSelectOperators,
  getGridStringOperators,
  gridClasses,
} from "@mui/x-data-grid-pro";
import {
  CustomerPaymentStatus,
  currencyFormatter,
  toDecimal,
} from "shared";
import { Tooltip } from "@mui/material";

import { useCustomerPayments } from "../../hooks/useCustomerPayments";
import { useGetSelectedOrganisation } from "../../hooks/useGetSelectedOrganisation";
import { WrappedLink } from "../WrappedLink";
import { Badge } from "../../../common/Atoms/Badge";
import { Paragraph } from "../../../common/Atoms/Typography/Paragraph";
import { Heading } from "../../../common/Atoms/Typography/Heading";
import { MultiButtonCardHeader } from "../../../common/Atoms/MultiButtonCardHeader";
import { commonConfig, filterColumns } from "../../lib/datagrid";
import { useRetrySyncMutation } from "../../services/api/customerPaymentsApi/customerPayments";
import { DatagridCard } from "../Common/Datagrid/DatagridCard";

const statusOptions = Object.values(CustomerPaymentStatus)
  .map(ps => ({
    label: ps,
    value: ps,
  }));

function openLinkNewTab(url: string) {
  window.open(url, `_blank`, `noreferrer noopener`);
}

export function CustomerPaymentsRoot() {
  const currentOrg = useGetSelectedOrganisation();
  const {
    data: customerPayments,
    isLoading,
    isFetching,
    count,
    filterModel,
    sortModel,
    paginationModel,
    setFilterModel,
    setSortModel,
    setPaginationModel,
  } = useCustomerPayments();
  const [ retrySync, { isLoading: isRetryingSync } ] = useRetrySyncMutation();

  const rows = useMemo(() => {
    const result = customerPayments?.customerPayments.map(cp => {
      // Synced accounting payments are the source of truth; until they exist, show the allocations recorded at checkout
      const syncedRows = cp.childPayments.flatMap(child => child.invoicePayments.map(inv => ({
        key: `invoice-payment-${inv.id}`,
        invoiceId: inv.invoiceId,
        invoiceNumber: inv.invoice.number,
        amountCents: inv.amountCents,
        currency: cp.originalCurrency,
      })));

      const allocationRows = (cp.invoiceAllocations || []).map(a => ({
        key: `allocation-${a.stripePaymentId}-${a.invoiceId}`,
        invoiceId: a.invoiceId,
        invoiceNumber: a.invoiceNumber,
        amountCents: a.amountCents,
        currency: a.currency,
      }));

      return {
        ...cp,
        invoiceRows: syncedRows.length ? syncedRows : allocationRows,
      };
    });

    return result || [];
  }, [customerPayments?.customerPayments]);

  function retrySyncPayment(id: number) {
    if (!currentOrg) {
      return;
    }

    retrySync({
      organisationId: currentOrg.id,
      customerPaymentId: id,
    });
  }

  const columns: GridColDef<(typeof rows)[number]>[] = useMemo(() => {
    const r: GridColDef<(typeof rows)[number]>[] = [
      {
        field: `id`,
        filterable: false,
        headerName: `ID`,
        type: `number`,
      },
      {
        field: `paymentDateTime`,
        filterable: false,
        headerName: `Timestamp`,
        description: `The time the payment was started or completed at`,
        type: `string`,
        renderCell: ({ row }) => row.paymentDateTime ? (
          <>{ new Date(row.paymentDateTime).toLocaleString() }</>
        ) : <>{ new Date(row.createdAt).toLocaleString() }</>,
      },
      {
        field: `updatedAt`,
        filterable: false,
        headerName: `Last Updated`,
        description: `The last time anything on the payment changed`,
        type: `string`,
        renderCell: ({ row }) => <>{ new Date(row.updatedAt).toLocaleString() }</>,
      },
      {
        field: `contact`,
        headerName: `Contact`,
        type: `string`,
        minWidth: 150,
        // flex: 0.75,
        filterOperators: getGridStringOperators().filter(op => op.value === `contains`),
        sortable: false,
        renderCell: ({ row }) =>  (
          <WrappedLink to={ `/contacts/${row.contact.id}` }>
            <span className={ `text-blue-600 underline whitespace-wrap` }>
              { row.contact.name }
            </span>
          </WrappedLink>
        ),
      },
      {
        field: `status`,
        headerName: `Status`,
        type: `singleSelect`,
        filterOperators: getGridSingleSelectOperators().filter(op => op.value === `is`),
        valueOptions: statusOptions,
        width: 110,
        renderCell: ({ row }) => {
          if (row.status === CustomerPaymentStatus.PAID) {
            if (row.accountingSyncFailed) {
              return (
                <Badge
                  className={ `w-fit` }
                  color={ `red` }
                >
                  { `Paid & Sync Failed` }
                </Badge>
              );
            }

            return (
              <Badge
                className={ `w-fit` }
                color={ `green` }
              >
                { `Paid` }
              </Badge>
            );
          }

          if (row.status === CustomerPaymentStatus.STARTED) {
            return (
              <Tooltip title={ `The contact recently started a checkout` }>
                <Badge
                  className={ `w-fit` }
                  color={ `yellow` }
                >
                  { `Started` }
                </Badge>
              </Tooltip>
            );
          }

          if (row.status === CustomerPaymentStatus.ABANDONED) {
            return (
              <Tooltip title={ `The contact started a checkout but has not completed` }>
                <Badge
                  className={ `w-fit` }
                  color={ `red` }
                >
                  { `Abandoned` }
                </Badge>
              </Tooltip>
            );
          }

          if (row.status === CustomerPaymentStatus.PROCESSING) {
            return (
              <Tooltip title={ `The payment is pending settlement.` }>
                <Badge
                  className={ `w-fit` }
                  color={ `yellow` }
                >
                  { `Processing` }
                </Badge>
              </Tooltip>
            );
          }

          if (row.status === CustomerPaymentStatus.REQUIRES_ACTION) {
            return (
              <Tooltip title={ `Waiting on the contact, for example to approve the payment with their bank or verify their bank account.` }>
                <Badge
                  className={ `w-fit` }
                  color={ `yellow` }
                >
                  { `Action Required` }
                </Badge>
              </Tooltip>
            );
          }

          if (row.status === CustomerPaymentStatus.EXPIRED) {
            return (
              <Tooltip title={ `The checkout session expired before the contact completed payment.` }>
                <Badge
                  className={ `w-fit` }
                  color={ `red` }
                >
                  { `Expired` }
                </Badge>
              </Tooltip>
            );
          }

          if (row.status === CustomerPaymentStatus.FAILED) {
            return (
              <Tooltip title={ row.errorMessage || `The payment failed to process.` }>
                <Badge
                  className={ `w-fit` }
                  color={ `red` }
                >
                  { `Failed` }
                </Badge>
              </Tooltip>
            );
          }

          if (row.status === CustomerPaymentStatus.UNKNOWN) {
            return (
              <Tooltip title={ `An unknown error occurred during payment processing.` }>
                <Badge
                  className={ `w-fit` }
                  color={ `gray` }
                >
                  { `Unknown` }
                </Badge>
              </Tooltip>
            );
          }
        },
      },
      {
        field: `originalCurrency`,
        filterable: false,
        headerName: `Inv Currency`,
        description: `The currency the invoice was issued in`,
        type: `string`,
        maxWidth: 90,
      },
      {
        field: `transactionCurrency`,
        filterable: false,
        headerName: `TX Currency`,
        type: `string`,
        maxWidth: 90,
        description: `The currency Stripe converted the amounts to.`,
      },
      {
        field: `totalAmountCentsOriginalCurrency`,
        filterable: false,
        headerName: `Total Amount Inv Currency`,
        type: `number`,
        renderCell: ({ row }) => (
          <span>
            { currencyFormatter(row.originalCurrency)(toDecimal(row.totalAmountCentsOriginalCurrency)) }
          </span>
        ),
      },
      {
        field: `totalAmountCentsTransactionCurrency`,
        filterable: false,
        headerName: `Total Amount TX Currency`,
        type: `number`,
        renderCell: ({ row }) => (
          <span>
            { currencyFormatter(row.transactionCurrency)(toDecimal(row.totalAmountCentsTransactionCurrency)) }
          </span>
        ),
      },
      {
        field: `surchargeAmountCentsOriginalCurrency`,
        filterable: false,
        headerName: `Surcharge Revenue`,
        type: `number`,
        renderCell: ({ row }) => (
          <span>
            { currencyFormatter(row.originalCurrency)(toDecimal(row.surchargeAmountCentsOriginalCurrency)) }
          </span>
        ),
      },
      {
        field: `stripeFeeCentsTransactionCurrency`,
        filterable: false,
        headerName: `Stripe Fee`,
        type: `number`,
        renderCell: ({ row }) => (
          <span>
            { `(` }
            { currencyFormatter(row.transactionCurrency)(toDecimal(row.stripeFeeCentsTransactionCurrency)) }
            { `)` }
          </span>
        ),
      },
      {
        field: `actions`,
        type: `actions`,
        width: 80,
        getActions: ({ row }) => {
          const actions: ReactElement<GridActionsCellItemProps>[] = [];

          if (row.stripePaymentIntentId) {
            actions.push(
              <GridActionsCellItem
                key={ `stripe-link-${row.id}` }
                label={ `View in Stripe` }
                onClick={ () => openLinkNewTab(`https://dashboard.stripe.com/payments/${row.stripePaymentIntentId}`) }
                showInMenu
              />,
            );
          }

          if (row.receiptUrl) {
            actions.push(
              <GridActionsCellItem
                key={ `receipt-link-${row.id}` }
                label={ `View Receipt` }
                onClick={ () => openLinkNewTab(row.receiptUrl) }
                showInMenu
              />,
            );
          }

          if (row.accountingSyncFailed) {
            actions.push(
              <GridActionsCellItem
                key={ `retry-sync-${row.id}` }
                label={ `Retry Sync` }
                onClick={ () => retrySyncPayment(row.id) }
                showInMenu
              />,
            );
          }

          return actions;
        },
      },

    ];

    // Apply some duplicates
    return r.map(c => ({
      ...c,
      editable: false,
      headerClassName: `text-md text-gray-800 font-bold`,
    }));
  }, [rows]);

  const rowDetail = useCallback(({ row }: {row: (typeof rows)[number]}) => {
    if (!row.invoiceRows.length) {
      return null;
    }

    return (
      <div className={ `mb-2` }>
        <Heading
          size={ `sm` }
          className={ `border-b border-gray-300 mb-2` }
        >
          { `Invoices` }
        </Heading>
        {
          row.invoiceRows.map(inv => (
            <div
              key={ inv.key }
              className={ `ml-6 grid grid-cols-2 gap-32 border-b border-gray-200 p-1 w-fit` }
            >
              <Paragraph
                variant={ `link` }
                className={ `font-bold` }
              >
                <WrappedLink
                  to={ `/invoices/${inv.invoiceId}` }
                >
                  { inv.invoiceNumber }
                </WrappedLink>
              </Paragraph>

              <Paragraph>
                { currencyFormatter(inv.currency)(toDecimal(inv.amountCents)) }
              </Paragraph>
            </div>
          ))
        }
      </div>
    );
  }, []);

  const getTogglableColumns = (columns: GridColDef[]) => {
    return columns
      .filter(column => column.field !== `id`)
      .filter(column => column.type !== `actions`)
      .map(column => column.field);
  };

  if (!currentOrg?.stripeAccount) {
    // No stripe account, disable

    return null;
  }

  const viewportHeight = window.innerHeight;
  const tableHeight = viewportHeight
    - 180; // px for the header

  return (
    <div>
      <MultiButtonCardHeader
        title={ `Stripe Payments` }
        variant={ `plain` }
        description= { `Payments made by customers using Stripe via Paidnice. Expand the row to see which invoices payment was applied against.` }
        secondaryDescription={ `Only Stripe payments made on the Paidnice portal are shown here. Payments made directly on other platforms are not included.` }
        badge={ `Early Access` }
        badgeColor={ `purple` }
        badgeTooltip={ `You have early access to integrated Stripe payments. Please let us know if there is anything we can improve on!` }
      />
      <DatagridCard className={ `mt-4` }>
        <div
          style={ { height: tableHeight } }
        >
          <DataGridPro
            sx={ {
              ...commonConfig.headerStyle,
              border: `none`,
              [`& .${gridClasses.cell}`]: {
                py: 1,
              },
            } }
            initialState={ {
              columns: {
                columnVisibilityModel: {
                  id: false,
                },
              },
            } }
            rows={ rows }
            autosizeOnMount
            autosizeOptions={ {
              expand: true,
            } }
            getRowHeight={ () => `auto` }
            columns={ columns }
            loading={ isLoading || isFetching || isRetryingSync }
            pageSizeOptions={ [15, 20, 50, 100, 300] }
            disableDensitySelector
            // Pagination
            pagination
            paginationMode={ `server` }
            rowCount={ count }
            paginationModel={ paginationModel }
            onPaginationModelChange={ setPaginationModel }
            // Filter
            filterDebounceMs={ 300 }
            filterMode={ `server` }
            filterModel={ filterModel }
            onFilterModelChange={ setFilterModel }
            // Sort
            sortingMode={ `server` }
            sortModel={ sortModel }
            onSortModelChange={ setSortModel }
            // Expanded rows for invoice detail
            getDetailPanelContent={ rowDetail }
            getDetailPanelHeight={ () => `auto` }
            // Slots
            showToolbar
            slots={ {
              toolbar: CustomToolbar,

            } }
            slotProps={ {
              pagination: {
                showFirstButton: true,
                showLastButton: true,
              },
              filterPanel: {
                filterFormProps: {
                  filterColumns: args => filterColumns(args, [`id`]),
                },
              },
              columnsManagement: {
                getTogglableColumns,
              },
            } }
          />
        </div>
      </DatagridCard>
    </div>
  );
}

function CustomToolbar() {
  return (
    // The underlying Toolbar right-aligns its items by default
    <GridToolbarContainer sx={ { justifyContent: `flex-start` } }>
      { /* <div className={ `flex justify-between w-full` }> */ }
      <GridToolbarQuickFilter
        // Search the whole term, not each word separately
        quickFilterParser={ value => value.trim() ? [value.trim()] : [] }
        debounceMs={ 350 }
        slotProps={ { root: { placeholder: `Search contact, invoice or Stripe ID...` } } }
      />

      <GridToolbarFilterButton />

      <GridToolbarColumnsButton />

      { /* <GridToolbarExport
          slotProps={ {
            tooltip: { title: `Export data` },
            button: { variant: `outlined` },
          } }
        /> */ }
      { /* </div> */ }
    </GridToolbarContainer>
  );
}
